import React, { useState, useEffect } from 'react';
import axiosInstance from '../../api/axiosConfig';

export default function ConfigureTab() {
    const [settings, setSettings] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');

    useEffect(() => {
        axiosInstance.get('/api/working-time/settings')
            .then((r) => setSettings(r.data.settings))
            .catch((e) => setError(e.response?.data?.message || 'Could not load settings'));
    }, []);

    const save = (event) => {
        event.preventDefault();
        setSaving(true);
        setError('');
        setMessage('');

        axiosInstance.put('/api/working-time/settings', settings)
            .then((r) => {
                setSettings(r.data.settings);
                setMessage(r.data.message);
            })
            .catch((e) => setError(e.response?.data?.message || 'Could not save settings'))
            .finally(() => setSaving(false));
    };

    if (!settings) {
        return <div className="wt-empty">{error || 'Loading…'}</div>;
    }

    return (
        <form onSubmit={save} style={{ maxWidth: 520 }}>
            {error && <div className="wt-error">{error}</div>}
            {message && <div className="wt-ok">{message}</div>}

            <div className="wt-field" style={{ marginBottom: 18 }}>
                <label htmlFor="wt-idle">Idle threshold</label>
                <select
                    id="wt-idle"
                    value={settings.idleThresholdSeconds}
                    onChange={(e) => setSettings({
                        ...settings, idleThresholdSeconds: Number(e.target.value)
                    })}
                >
                    <option value={300}>5 minutes</option>
                    <option value={600}>10 minutes</option>
                    <option value={900}>15 minutes</option>
                </select>
                <span className="wt-note">
                    Time without keyboard or mouse input before the employee counts as idle.
                    Running agents pick this up on their next sync — nothing to reinstall.
                </span>
            </div>

            <div className="wt-field" style={{ marginBottom: 18 }}>
                <label htmlFor="wt-beat">Heartbeat interval (seconds)</label>
                <input
                    id="wt-beat"
                    type="number"
                    min={20}
                    max={3600}
                    value={settings.heartbeatSeconds}
                    onChange={(e) => setSettings({
                        ...settings, heartbeatSeconds: Number(e.target.value)
                    })}
                />
                <span className="wt-note">
                    How often each laptop reports. Lower means fresher figures and more requests.
                    Values outside 20–3600 are clamped, because the agent ignores them.
                </span>
            </div>

            <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '4px 0 22px' }} />
            <h3 style={{ fontSize: 14, margin: '0 0 4px', color: '#0f172a' }}>Attendance sheet</h3>
            <p className="wt-note" style={{ marginTop: 0, marginBottom: 16 }}>
                Governs the Working Time → Sheet tab only. Office hours are the same clock
                hours for every employee, including remote staff — everyone is judged against
                Qatar time, not their own local day.
            </p>

            <div className="wt-field" style={{ marginBottom: 18 }}>
                <label htmlFor="wt-office-hours">Office hours (Qatar time)</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input
                        id="wt-office-hours"
                        type="number"
                        min={0}
                        max={23}
                        style={{ width: 70 }}
                        value={settings.officeStartHour}
                        onChange={(e) => setSettings({
                            ...settings, officeStartHour: Number(e.target.value)
                        })}
                    />
                    <span>:00 to</span>
                    <input
                        type="number"
                        min={1}
                        max={24}
                        style={{ width: 70 }}
                        value={settings.officeEndHour}
                        onChange={(e) => setSettings({
                            ...settings, officeEndHour: Number(e.target.value)
                        })}
                    />
                    <span>:00</span>
                </div>
                <span className="wt-note">
                    Only time inside this window counts toward attendance hours on the Sheet tab.
                </span>
            </div>

            <div className="wt-field" style={{ marginBottom: 18 }}>
                <label htmlFor="wt-max-daily">Maximum counted hours per day</label>
                <input
                    id="wt-max-daily"
                    type="number"
                    min={1}
                    max={24}
                    style={{ width: 90 }}
                    value={settings.maxDailyHours}
                    onChange={(e) => setSettings({
                        ...settings, maxDailyHours: Number(e.target.value)
                    })}
                />
                <span className="wt-note">
                    A day can never count for more than this, even if the office window is longer.
                </span>
            </div>

            <div className="wt-field" style={{ marginBottom: 18 }}>
                <label htmlFor="wt-half-day">Half Day threshold (hours)</label>
                <input
                    id="wt-half-day"
                    type="number"
                    min={0}
                    max={24}
                    style={{ width: 90 }}
                    value={settings.halfDayThresholdHours}
                    onChange={(e) => setSettings({
                        ...settings, halfDayThresholdHours: Number(e.target.value)
                    })}
                />
                <span className="wt-note">
                    Counted hours at or above this are Half Day; below it, Absent.
                </span>
            </div>

            <div className="wt-field" style={{ marginBottom: 22 }}>
                <label htmlFor="wt-present">Present threshold (hours)</label>
                <input
                    id="wt-present"
                    type="number"
                    min={0}
                    max={24}
                    style={{ width: 90 }}
                    value={settings.presentThresholdHours}
                    onChange={(e) => setSettings({
                        ...settings, presentThresholdHours: Number(e.target.value)
                    })}
                />
                <span className="wt-note">
                    Counted hours at or above this are Present. Must stay at or above the
                    Half Day threshold to make sense.
                </span>
            </div>

            <div className="wt-field" style={{ marginBottom: 22 }}>
                <label htmlFor="wt-enroll">
                    <input
                        id="wt-enroll"
                        type="checkbox"
                        checked={settings.enrollmentOpen}
                        onChange={(e) => setSettings({
                            ...settings, enrollmentOpen: e.target.checked
                        })}
                        style={{ marginRight: 8 }}
                    />
                    Allow new devices to enroll
                </label>
                <span className="wt-note">
                    Turn this off once every laptop is set up, so no further machine can enroll itself.
                </span>
            </div>

            <button type="submit" className="wt-btn primary" disabled={saving}>
                {saving ? 'Saving…' : 'Save settings'}
            </button>
        </form>
    );
}
